<template>
  <div class="swa-product-listing-container" :data-entry-id="id">
    <!-- Mweb/Tablet -->
    <div
      v-if="isTablet && isClientRendered"
      class="swa-mweb-filters-modal-container"
    >
      <!-- Mweb filters teleport zone -->
    </div>

    <ProductListingSortFilterBarMweb
      v-if="isTablet && isClientRendered"
      class="is-mobile-and-tablet-only"
    >
      <template #filter-btn>
        <ProductListingFilterMweb
          :filter-data="productFilter"
          :is-variant-fetching="variantFetching"
          :variant-count="variantData?.transformedMeta.total"
        />
      </template>
      <template #sort-btn>
        <ProductListingSortMweb
          class="mweb-filter-sort-item"
          :sort-options="sortOptions"
          @update:selected-option="onSortChange"
        />
      </template>
    </ProductListingSortFilterBarMweb>

    <!-- Web Desktop -->
    <div class="swa-web-product-listing section">
      <UiLoader v-if="!isClientRendered" :size="'80px'" :width="'1px'" />
      <div v-else class="container">
        <div v-if="!isTablet" class="web-filter-container is-desktop-and-above">
          <ProductListingFilterWeb :filter-data="productFilter" />
        </div>

        <div class="listing-container">
          <ProductListingSort
            class="is-desktop-and-above"
            :sort-options="sortOptions"
            @update:selected-option="onSortChange"
          />
          <div v-if="variantFetching" class="loader-container">
            <UiLoader :size="'80px'" :width="'3px'" />
          </div>
          <ProductListingGrid
            :product-listings="productsAndBanners"
            :pagination="pagination"
            :component-position="props.componentPosition"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import UiLoader from '@sephora-asia/ui-loader'
import type { ProductListingBannerDTO } from '~/types/api/bff/web/contentful/component.types'
import type { ProductListingContainerProps } from '~/types/components/contentful/components.types'
import type {
  ListingBanner,
  ListingProduct,
  ProductListings,
} from '~/types/components/product-listing.types'
import type { LocationQueryRaw } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { useProductListingSortStore } from '~/stores/product-listing/sort-store'
import { useProductListingPaginationStore } from '~/stores/product-listing/pagination-store'
import {
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
} from '~/composables/utils/product-listing/pagination/constants'

const props = defineProps<ProductListingContainerProps>()

const route = useRoute()
const router = useRouter()
const { queryFilterType } = useQueryHelper()
const {
  updateProductListingRoute,
  getAllQueriesFromRoute,
  getCategoryPathFromRoute,
} = useProductListingRoutes()

const { trackEvent } = useGtmTrack()

/**
 * - - - - - States - - - - -
 */
const pageSizeQuery = ref<number>(
  Number(route.query.page) > DEFAULT_PAGE
    ? Number(route.query.page) * DEFAULT_PER_PAGE
    : DEFAULT_PER_PAGE
)
const pageNumberQuery = ref(
  pageSizeQuery.value > DEFAULT_PER_PAGE
    ? DEFAULT_PAGE
    : Number(route.query.page) || DEFAULT_PAGE
)

const isTablet = useMediaQuery('(max-width: 1023px)')

// Show loader during SSR to prevent flickering of filter component in UI when transitioning from SSR to CSR.
// Remove workaround once all filter components are aligned to use shared state and methods
const isClientRendered = ref(false)

/**
 * - - - - - Filters - - - - -
 */
const filterStore = useProductListingFiltersStore()
const {
  selectedFilterParams,
  initSelectedFiltersState,
  setSelectedFilterParams,
  setSelectedCategory,
} = filterStore

const productGroupSlugUrl = computed(() => props.productGroupSlugUrl ?? '')

const { data: productFilterData, suspense: productFilterSuspense } = useQuery(
  useGetProductFiltersQuery(productGroupSlugUrl)
)
await productFilterSuspense()

const productFilter = computed(
  () =>
    productFilterData.value ?? {
      categoryFilters: [],
      brandFilters: [],
      priceRangeFilter: {
        minPrice: 100,
        maxPrice: 10000,
      },
      otherFilters: [],
      typeFilters: [],
    }
)

/**
 * - - - - - Variants - - - - -
 */
const {
  data: variantData,
  suspense: variantSuspense,
  isFetching: variantFetching,
} = useQuery(
  useGetVariantsQuery(
    pageSizeQuery,
    pageNumberQuery,
    productGroupSlugUrl,
    productFilter
  )
)
await variantSuspense()

const products = ref<ListingProduct[]>([])

const totalProducts: ComputedRef<number> = computed((previous = 0) => {
  if (variantFetching.value) {
    return previous
  }

  return variantData.value?.transformedMeta?.totalProducts ?? 0
})

/**
 * - - - - - Sort - - - - -
 */
const sortStore = useProductListingSortStore()
const { DEFAULT_SORT, isValidSort, updateSortByOptionsDefault } = sortStore
const sortOptions = computed(() => sortStore.sortOptions)

function onSortChange(selectedSortOption: string) {
  updateSortByOptionsDefault(selectedSortOption)
  useWatcherManager('sort', async () => {
    await updateProductListingRoute({ sortQuery: true })
    resetPageQuery()
  })
}

/**
 * - - - - - Pagination/Listing - - - - -
 */
const { hasRoomForMoreProducts, isValidPage, onPageChange } =
  useProductListingPaginationStore()

const currentPage = computed(() => Number(route.query.page) || DEFAULT_PAGE)

const visibleProducts = computed(() =>
  Math.min(currentPage.value * DEFAULT_PER_PAGE, totalProducts.value)
)

const pagination = computed(() => ({
  visibleProducts: visibleProducts.value,
  totalProducts: totalProducts.value,
  isButtonVisible: hasRoomForMoreProducts(
    visibleProducts.value,
    totalProducts.value
  ),
  onShowMoreProducts,
}))

function onShowMoreProducts() {
  if (!hasRoomForMoreProducts(visibleProducts.value, totalProducts.value))
    return

  const currentPageValue = currentPage.value + 1
  pageSizeQuery.value = DEFAULT_PER_PAGE
  pageNumberQuery.value = currentPageValue

  onPageChange(currentPageValue)
}

function resetPageQuery() {
  pageNumberQuery.value = DEFAULT_PAGE
  pageSizeQuery.value = DEFAULT_PER_PAGE
}

function getCorrectQuery(query: LocationQueryRaw): LocationQueryRaw {
  const sort = (query.sort ?? DEFAULT_SORT).toString()
  const minPrice = Number(query['min-price'] ?? 0)
  const maxPrice = Number(query['max-price'] ?? 0)

  const newQuery: LocationQueryRaw = { ...query }

  if (sort === DEFAULT_SORT || !isValidSort(sort)) delete newQuery.sort
  if (minPrice <= 0) delete newQuery['min-price']
  if (maxPrice <= 0) delete newQuery['max-price']
  if (
    Number(newQuery.page) <= DEFAULT_PAGE ||
    !isValidPage(totalProducts.value)
  ) {
    delete newQuery.page
  }

  return Object.fromEntries(
    Object.entries(newQuery).filter(([_, value]) => !!value)
  ) as LocationQueryRaw
}

const initialBannerIndex = computed(() => props.initialBannerPosition - 1)
const bannerOffsetIndex = computed(() => props.bannerOffsetCount - 1)

const productListingBanners: ComputedRef<ListingBanner[]> = computed(
  () =>
    props.productListingBanners?.map(
      (listingBanner: ProductListingBannerDTO) => ({
        type: 'banner',
        id: listingBanner.id,
        img: {
          alt: listingBanner.altText ?? '',
          src: listingBanner.banner.src ?? '',
          srcMobile: listingBanner.banner.srcMobile ?? '',
        },
        urlPath: listingBanner.urlPath,
        isOpenInNewTab: listingBanner.isOpenInNewTab,
        internalName: listingBanner.internalName,
      })
    ) ?? []
)

const productsAndBanners: ComputedRef<ProductListings[]> = computed(() => {
  const productListingBannerLength = productListingBanners.value.length
  const productsLength = products.value.length

  if (!productsLength) {
    return []
  } else if (!productListingBannerLength && productsLength) {
    return products.value
  }

  let bannerCount = 0
  let insertBannerIndex = initialBannerIndex.value

  const numberOfProductsToShowAllBanners =
    initialBannerIndex.value +
    bannerOffsetIndex.value * (productListingBannerLength - 1)

  const listing = products.value.reduce(
    (listing: ProductListings[], product: ListingProduct, index: number) => {
      if (
        insertBannerIndex === index &&
        bannerCount < productListingBannerLength
      ) {
        insertBannerIndex += bannerOffsetIndex.value
        // if no change to insertBannerIndex i.e bannerOffsetIndex === 0
        if (insertBannerIndex === initialBannerIndex.value) {
          listing = listing.concat(productListingBanners.value)
        } else {
          listing.push(productListingBanners.value[bannerCount])
          bannerCount += 1
        }
      }
      listing.push(product)
      return listing
    },
    []
  )

  if (
    variantData.value &&
    numberOfProductsToShowAllBanners > variantData.value.transformedMeta.total
  ) {
    listing.push(...productListingBanners.value.slice(bannerCount)) // Append remaining banners to end of list
  }

  return listing
})

/**
 * - - - - - Methods - - - - -
 */
const lockerOccupancy: string[] = []
// Used to prevent infinite loops between watchers,
// by allowing same-key functions to lock another out.
// All participating functions will use the same locker within useWatcherManager.
// Once the locker is occupied, only functions with the correct locker-key can run
async function useWatcherManager(
  lockerKey: string,
  callback: () => Promise<void> | void
) {
  if (!lockerOccupancy.length || lockerOccupancy[0] === lockerKey) {
    lockerOccupancy.push(lockerKey)
  } else return

  await callback()
  await nextTick()
  lockerOccupancy.pop()
}

/**
 * - - - - - Data tracking - - - - -
 */

function trackProductRefreshed() {
  // Defaulting to 'product_group' before categories/brands are properly set up
  const pageType = queryFilterType.value

  const { topCategory, category, subCategory } = route.params

  trackEvent({
    event: 'listing_refresh',
    params: {
      event_category: 'List',
      event_action: 'Refreshed',
      event_label: pageType,
      cd8: topCategory || 'none',
      cd9: category || 'none',
      cd10: subCategory || 'none',
      cd11: pageType === 'brand' ? pageType : 'none',
      cd17: variantData.value?.transformedMeta.totalProducts,
      cd18: variantData.value?.transformedMeta.perPage,
      cd19: route.query.sort?.toString() || DEFAULT_SORT,
      cd20: variantData.value?.transformedMeta.currentPage,
      new_product_list: variantData.value?.transformedData?.map(
        (product, index) => ({
          name: product.productName,
          sku: product.id,
          brand: product.brandName,
          position: index + 1,
          out_of_stock: product.soldOut,
        })
      ),
    },
  })
}

/**
 * - - - - - Lifecycle - - - - -
 */
onBeforeMount(() => {
  if (currentPage.value > DEFAULT_PAGE) {
    pageSizeQuery.value = DEFAULT_PER_PAGE * currentPage.value
  }
})

onMounted(() => {
  const query = getCorrectQuery({ ...route.query })
  if (Object.keys(route.query).length !== Object.keys(query).length) {
    router.replace({ query })
  }
})

onMounted(() => {
  isClientRendered.value = true
})

/**
 * - - - - - Watchers - - - - -
 * NOTE: Order of watchers is important; DO NOT rearrange order unless necessary
 */
// State Watcher:
// Combining selectedCategory and selectedFilterParams to prevent bugs when clearing both at once
watch(
  () => [filterStore.selectedCategory, selectedFilterParams],
  () => {
    // Note: to uncomment when implementing category pages
    // (newVal, oldVal) => {
    //   const isCategoryChanged = newVal[0] !== oldVal[0]
    //   Allows pushed route(without type filters) to update state
    //   const lockerKey = isCategoryChanged
    //     ? 'route-updates-state'
    //     : 'state-updates-route'

    useWatcherManager('state-updates-route', async () => {
      await updateProductListingRoute({
        categoryParams: true,
        filterQuery: true,
        // withoutTypeFilters: isCategoryChanged,
      })
      resetPageQuery()
    })
  },
  { deep: true }
)

watch(
  () => productFilter,
  () => {
    useWatcherManager('route-updates-state', () => {
      initSelectedFiltersState(productFilter.value)
    })
  },
  { immediate: true }
)

watch(
  () => route.query,
  () => {
    useWatcherManager('route-updates-state', () => {
      updateSortByOptionsDefault(route.query.sort?.toString() || DEFAULT_SORT)
      setSelectedFilterParams(getAllQueriesFromRoute())
      setSelectedCategory(getCategoryPathFromRoute())
    })
  },
  { immediate: true }
)

watch(
  variantData,
  newData => {
    if (!newData) return

    if (
      Number(route.query.page) > DEFAULT_PAGE &&
      pageSizeQuery.value === DEFAULT_PER_PAGE
    ) {
      products.value = [...products.value, ...newData.transformedData]
    } else {
      products.value = newData.transformedData
    }

    trackProductRefreshed()
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.swa-product-listing-container {
  display: grid;
  gap: inherit; // Inherit from landing pages container; changes depending on media query

  .swa-web-product-listing {
    .container {
      overflow: auto;
      overflow-wrap: break-word;
      display: flex;
      column-gap: 24px;

      .filter-container {
        max-width: 312px;
        flex: 1;
      }

      .listing-container {
        flex: 1;

        .loader-container {
          display: flex;
          justify-content: center;
        }
      }

      .loader-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        position: fixed;
        top: 0;
        left: 0;
        z-index: var(--size-z-index-sticky);
        background-color: var(--color-base-white);
        opacity: 0.6;
      }
    }
  }
}
</style>
